import React, { useEffect, useState, useCallback } from "react";
import { Select, Spin } from "antd";
import DebounceSelect from "@/component/DebounceSelect";
import { request } from '@/utils/useRequest';
import { useCacheStore } from "@/store/useCacheStore";

const EntityUserSelect = ({ value, onChange, multiple = false, disabled = false, initial = false }) => {
    const getCache = useCacheStore.use.get();
    const setCache = useCacheStore.use.set();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    // 标准化用户数据格式
    const normalizeUserData = (user) => ({
        label: `${user.Name}(${user.Phone ?? '-'})`,
        value: String(user.Id), // 确保value是字符串类型
        Id: user.Id,
        Username: user.Username,
        Name: user.Name,
        Phone: user.Phone,
        Nickname: user.Nickname
    });

    // 处理选择变更
    const handleChange = (selectedValue, option) => {
        if (!onChange) return;

        if (multiple) {
            // 多选模式：返回用户实体数组
            // 从selectedValue和options中重新构建完整的用户实体
            const entities = Array.isArray(selectedValue) ? selectedValue.map(selectedItem => {
                // selectedItem可能是{value, label}格式或者直接是ID
                const valueId = typeof selectedItem === 'object' ? selectedItem.value : selectedItem;
                
                // 先尝试从option中获取
                const optionItem = Array.isArray(option) ? option.find(opt => 
                    String(opt.value || opt.Id) === String(valueId)
                ) : null;
                
                if (optionItem && optionItem.Id) {
                    return {
                        Id: optionItem.Id,
                        Name: optionItem.Name || optionItem.label?.split('(')[0],
                        Username: optionItem.Username,
                        Phone: optionItem.Phone,
                        Nickname: optionItem.Nickname
                    };
                }
                
                // 如果option中没有完整信息，从options状态中查找
                const optionFromState = options.find(opt => String(opt.value || opt.Id) === String(valueId));
                if (optionFromState) {
                    return {
                        Id: optionFromState.Id,
                        Name: optionFromState.Name || optionFromState.label?.split('(')[0],
                        Username: optionFromState.Username,
                        Phone: optionFromState.Phone,
                        Nickname: optionFromState.Nickname
                    };
                }
                
                // 最后的兜底方案，只有ID
                return {
                    Id: valueId,
                    Name: `用户${valueId}`,
                    Username: undefined,
                    Phone: undefined,
                    Nickname: undefined
                };
            }).filter(Boolean) : [];
            onChange(entities);
        } else {
            // 单选模式：返回用户实体对象
            if (!selectedValue) {
                onChange(null);
                return;
            }
            
            // 先尝试从option中获取
            if (option && option.Id) {
                const entity = {
                    Id: option.Id,
                    Name: option.Name || option.label?.split('(')[0],
                    Username: option.Username,
                    Phone: option.Phone,
                    Nickname: option.Nickname
                };
                onChange(entity);
                return;
            }
            
            // 如果option中没有完整信息，从options状态中查找
            const optionFromState = options.find(opt => String(opt.value || opt.Id) === String(selectedValue));
            if (optionFromState) {
                const entity = {
                    Id: optionFromState.Id,
                    Name: optionFromState.Name || optionFromState.label?.split('(')[0],
                    Username: optionFromState.Username,
                    Phone: optionFromState.Phone,
                    Nickname: optionFromState.Nickname
                };
                onChange(entity);
                return;
            }
            
            // 最后的兜底方案
            const entity = {
                Id: selectedValue,
                Name: `用户${selectedValue}`,
                Username: undefined,
                Phone: undefined,
                Nickname: undefined
            };
            onChange(entity);
        }
    };

    // 获取用户列表
    const fetchUserList = useCallback(async (username = '') => {
        try {
            const result = await request('/api/v2/private/user/list', "POST", "json", {
                filters: {
                    keywords: username
                },
                pagination: {
                    current: 1,
                    pageSize: 30,
                }
            });
            if (result.success) {
                return result.data.map(normalizeUserData);
            }
        } catch (error) {
            console.error('获取用户列表失败:', error);
        }
        return [];
    }, []);

    // 根据用户ID获取用户详情（用于回显）
    const fetchUsersByIds = useCallback(async (userIds) => {
        if (!userIds || userIds.length === 0) return [];
        
        try {
            const result = await request('/api/v2/private/user/list', "POST", "json", {
                filters: {
                    userIds: Array.isArray(userIds) ? userIds : [userIds]
                },
                pagination: {
                    current: 1,
                    pageSize: 100,
                }
            });
            if (result.success) {
                return result.data.map(normalizeUserData);
            }
        } catch (error) {
            console.error('获取用户详情失败:', error);
        }
        return [];
    }, []);

    // 初始化加载用户列表
    useEffect(() => {
        if (initial) {
            const cacheKey = `user-options-${multiple ? 'multiple' : 'single'}-all`;
            const cachedData = getCache(cacheKey);
            
            if (cachedData) {
                setOptions(cachedData);
            } else {
                setLoading(true);
                fetchUserList('').then(data => {
                    setOptions(data);
                    setCache(cacheKey, data);
                }).finally(() => {
                    setLoading(false);
                });
            }
        }
    }, [initial, fetchUserList, getCache, setCache]);

    // 处理值回显
    useEffect(() => {
        if (!value || initial) return;

        const loadValueOptions = async () => {
            let userIds = [];
            
            if (multiple && Array.isArray(value)) {
                userIds = value.map(item => typeof item === 'object' ? item.Id : item).filter(Boolean);
            } else if (!multiple && value) {
                const userId = typeof value === 'object' ? value.Id : value;
                if (userId) userIds = [userId];
            }

            if (userIds.length > 0) {
                const userOptions = await fetchUsersByIds(userIds);
                // 直接设置选项，确保回显值能正确显示
                setOptions(userOptions);
            }
        };

        loadValueOptions();
    }, [value, multiple, initial, fetchUsersByIds]);

    // 获取显示值
    const getDisplayValue = () => {
        if (!value) return multiple ? [] : undefined;
        
        if (multiple) {
            return Array.isArray(value) ? value.map(item => {
                const id = typeof item === 'object' ? item.Id : item;
                const name = typeof item === 'object' ? item.Name : undefined;
                
                // 确保id是有效的值
                if (!id && id !== 0) return null;
                
                // 对于DebounceSelect（labelInValue=true），返回{value, label}格式
                if (!initial) {
                    return {
                        value: String(id),
                        label: name || `用户${id}`
                    };
                }
                // 对于普通Select，返回字符串ID
                return String(id);
            }).filter(Boolean) : [];
        } else {
            const id = typeof value === 'object' ? value.Id : value;
            const name = typeof value === 'object' ? value.Name : undefined;
            
            // 确保id是有效的值
            if (!id && id !== 0) return undefined;
            
            // 对于DebounceSelect（labelInValue=true），返回{value, label}格式
            if (!initial) {
                return {
                    value: String(id),
                    label: name || `用户${id}`
                };
            }
            // 对于普通Select，返回字符串ID
            return String(id);
        }
    };

    // 初始化模式使用Select，动态搜索使用DebounceSelect
    if (initial) {
        return (
            <Select
                className="w-full"
                showSearch
                mode={multiple ? 'multiple' : undefined}
                disabled={disabled}
                value={getDisplayValue()}
                placeholder="输入名称或电话查询"
                onChange={handleChange}
                options={options}
                loading={loading}
                notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
                optionFilterProp="label"
                filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    const searchText = inputValue.toLowerCase();
                    return (
                        option.label?.toLowerCase().includes(searchText) ||
                        option.Phone?.includes(searchText) ||
                        option.Username?.toLowerCase().includes(searchText) ||
                        option.Nickname?.toLowerCase().includes(searchText)
                    );
                }}
            />
        );
    }

    return (
        <DebounceSelect
            className="w-full"
            showSearch
            mode={multiple ? 'multiple' : undefined}
            disabled={disabled}
            value={getDisplayValue()}
            placeholder="输入名称或电话查询"
            fetchOptions={fetchUserList}
            onChange={handleChange}
            options={options}
        />
    );
};

export default EntityUserSelect;